import React, { useState } from 'react';
import { Pagination, Icon, Popover } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { DateTime } from 'luxon';
import { RecentMeeting, AttendeePriority } from '../../interfaces/interface';
import { PRIORITY_COLORS } from '../../constants/EAHuddleConstants';
import { EmployeePopoverCard } from '../attendees/EmployeePopoverCard';
import { generateMultiBadgerUrl } from '../../utils/attendeeUtils';
import styles from './Sidebar.module.scss';

// ── Icons ──────────────────────────────────────────────────────────────────────

const MeetingPlusIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const LinkIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const EnvelopeIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4l6 4 6-4M2 4v8h12V4H2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── MeetingCard ────────────────────────────────────────────────────────────────

export interface MeetingCardProps {
  meeting: RecentMeeting;
  existingLoginNames: Set<string>;
  onFavoriteClick: (loginName: string, priority?: AttendeePriority) => void;
  onDelete: (meetingId: string, subject: string) => void;
  isReadOnly: boolean;
  isMeetingRegistered: boolean;
}

export const MeetingCard: React.FC<MeetingCardProps> = ({
  meeting,
  existingLoginNames,
  onFavoriteClick,
  onDelete,
  isReadOnly,
  isMeetingRegistered
}) => {
  const [attendeePage, setAttendeePage] = useState(1);
  const [copied, setCopied] = useState(false);
  const PAGE_SIZE = 10;
  const totalAttendeePages = Math.ceil(meeting.attendees.length / PAGE_SIZE);
  const pagedAttendees = meeting.attendees.slice((attendeePage - 1) * PAGE_SIZE, attendeePage * PAGE_SIZE);

  return (
    <div className={styles.meetingCard} data-meeting-card>
      <div className={styles.meetingCardContent}>
        <p className={styles.meetingSubject}>
          <a href={meeting.tinyUrl} target="_blank" rel="noopener noreferrer" className={styles.meetingSubjectLink}>
            {meeting.subject}
          </a>
        </p>

        <span className={styles.meetingMeta}>
          {meeting.attendees.length > 0 ? (
            <Popover
              triggerType="custom"
              position="bottom"
              size="large"
              dismissButton={true}
              renderWithPortal
              content={
                <div>
                  <div className={styles.attendeePopoverHeader}>
                    <span className={styles.attendeePopoverTitle}>Attendees ({meeting.attendees.length})</span>
                    <a
                      href={generateMultiBadgerUrl(meeting.attendees.map((a) => a.loginName))}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.attendeePopoverLink}
                    >
                      Multi Badger ↗
                    </a>
                  </div>
                  <div className={styles.attendeeList}>
                    {pagedAttendees.map((attendee) => {
                      const isAlreadyAdded = existingLoginNames.has(attendee.loginName);
                      const addKey = attendee.isGroup ? `group:${attendee.loginName}` : attendee.loginName;
                      return (
                        <div key={attendee.loginName} className={styles.attendeeRow}>
                          {attendee.isGroup ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <div className={styles.groupIconSm}>
                                <EnvelopeIcon />
                              </div>
                              <span className={styles.groupLabelLink}>{attendee.loginName}</span>
                            </span>
                          ) : (
                            <EmployeePopoverCard loginName={attendee.loginName} renderWithPortal>
                              <Avatar
                                username={attendee.loginName}
                                size={AvatarSize.ExtraSmall}
                                imageAltText={`Profile image of ${attendee.loginName}`}
                              />
                            </EmployeePopoverCard>
                          )}
                          <div className={styles.attendeeActions}>
                            <span
                              className="priority-badge"
                              style={{
                                backgroundColor: PRIORITY_COLORS[attendee.priority]?.background,
                                color: PRIORITY_COLORS[attendee.priority]?.color
                              }}
                            >
                              {attendee.priority.charAt(0).toUpperCase() + attendee.priority.slice(1)}
                            </span>
                            {isAlreadyAdded ? (
                              <span style={{ color: 'var(--huddle-success)', display: 'inline-flex' }} title="Already added">
                                <Icon name="status-positive" size="small" />
                              </span>
                            ) : !isReadOnly && isMeetingRegistered ? (
                              <button
                                type="button"
                                className={styles.meetingActionButton}
                                aria-label={`Add ${attendee.loginName}${attendee.isGroup ? ' email list' : ''} to attendees as ${attendee.priority}`}
                                onClick={() => onFavoriteClick(addKey, attendee.priority)}
                              >
                                <MeetingPlusIcon />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {totalAttendeePages > 1 && (
                    <Pagination
                      currentPageIndex={attendeePage}
                      pagesCount={totalAttendeePages}
                      onChange={({ detail }) => setAttendeePage(detail.currentPageIndex)}
                    />
                  )}
                </div>
              }
            >
              <button type="button" className={styles.meetingAttendeeLink}>
                {meeting.attendees.length} attendee{meeting.attendees.length !== 1 ? 's' : ''}
              </button>
            </Popover>
          ) : (
            <span>0 attendees</span>
          )}{' '}
          · {DateTime.fromISO(meeting.createdAt).toRelative() ?? ''}
        </span>

        <div className={styles.meetingActions}>
          <div className={styles.copyButtonWrapper}>
            <button
              type="button"
              className={styles.meetingActionButton}
              aria-label={copied ? 'Link copied' : `Copy link for ${meeting.subject}`}
              onClick={() => {
                navigator.clipboard
                  .writeText(meeting.tinyUrl)
                  .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  })
                  .catch(() => {});
              }}
              title={copied ? 'Copied!' : 'Copy meeting link'}
            >
              {copied ? <Icon name="status-positive" size="small" /> : <LinkIcon />}
            </button>
            {copied && <span className={styles.copiedTooltip}>Copied!</span>}
          </div>
          <button
            type="button"
            className={styles.meetingDeleteButton}
            aria-label={`Delete ${meeting.subject}`}
            onClick={() => onDelete(meeting.meetingId, meeting.subject)}
          >
            <Icon name="remove" size="small" />
          </button>
        </div>
      </div>
    </div>
  );
};
